import React from 'react'
import { Bar } from 'react-chartjs-2'

const DeviceStatusStackedBarChart = ({
  isDarkTheme,
  handleModalShow,
  siteId,
  getcsvFileName,
  deviceStatus = [],
  SiteName
}) => {
  // Aggregate all device status data
  const allDeviceData = (deviceStatus || [])
    .map(widget => widget.WidgetData || {})
    .reduce((acc, curr) => {
      Object.entries(curr).forEach(([deviceType, statusObj]) => {
        if (!acc[deviceType]) acc[deviceType] = {};
        Object.entries(statusObj).forEach(([status, count]) => {
          acc[deviceType][status] = (acc[deviceType][status] || 0) + count;
        });
      });
      return acc;
    }, {});

  // Map deviceType to Datapoints for handleModalShow
  const deviceTypeToDatapoints = {};
    (deviceStatus || []).forEach(widget => {
    if (widget.Datapoints && widget.Datapoints.Interfacetypename) {
      // Map Interfacetypename(s)
      widget.Datapoints.Interfacetypename.forEach(type => {
        deviceTypeToDatapoints[type] = widget.Datapoints;
      });
      // Map WidgetData keys (e.g., "ACS Controller", "Camera") to Datapoints
      if (widget.WidgetData) {
        Object.keys(widget.WidgetData).forEach(type => {
          deviceTypeToDatapoints[type] = widget.Datapoints;
        });
      }
    }
    });

  const deviceTypes = Object.keys(allDeviceData);

  // Find all unique statuses across all device types
  const allStatusesSet = new Set();
  deviceTypes.forEach(deviceType => {
    Object.keys(allDeviceData[deviceType]).forEach(status => {
      allStatusesSet.add(status);
    });
  });
  const allStatuses = Array.from(allStatusesSet);

  // Assign colors for known statuses, fallback for others
  const statusColors = {
    Online: "#83ba2b",
    Offline: "#dd2121",
    "No Information": "#daa926",
    "Zone Alert": "#d06841",
    "Zone Restoral": "#4287f5",
    Faulty: "#b71c1c",
    "Normal": "#4caf50",
    "Non-Integrated": "#747474",
    "Integrated": "#0B70D1",
    "Unknown": "#131415",
    "Unavailable": "#454545"
  };
  const getColor = (status, idx) => statusColors[status] || `#${((1<<24)*Math.random()|0).toString(16)}`;

  // Prepare datasets for chart.js
  const datasets = allStatuses.map((status, idx) => ({
    label: status,
    backgroundColor: getColor(status, idx),
    data: deviceTypes.map(deviceType => allDeviceData[deviceType][status] || 0),
    barPercentage: 0.3,
    categoryPercentage: 1,
    borderRadius: 5,
    barThickness: 25,
    maxBarThickness: 30,
  }));

  const data = {
    labels: deviceTypes,
    datasets
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          boxWidth: 14,
          fontColor: '#474747',
          fontFamily: '7px Montserrat',
        },
      },
      animations: {
        tension: {
          duration: 1000,
          easing: 'active',
          from: 1,
          to: 0,
        }
      },
    },
    scales: {
      x: {
        stacked: true,
        border: {
          display: true,
          color: isDarkTheme ? '#bababa' : "#bababa",
        },
        grid: {
          display: false,
        },
        title: {
          display: true,
          text: "Devices",
          color: isDarkTheme ? "#000000" : "#6e6e6e",
          font: {
            size: 13,
            weight: 'bold',
          },
        },
        ticks: {
          color: isDarkTheme ? "" : "#bababa",
        },
      },
      y: {
        stacked: true,
        border: {
          display: true,
          color: isDarkTheme ? '#bababa' : "#bababa",
        },
        beginAtZero: true,
        title: {
          display: true,
          text: "Device status",
          color: isDarkTheme ? "#000000" : "#6e6e6e",
          font: {
            size: 13,
            weight: 'bold',
          },
        },
        grid: {
          display: false,
        },
        ticks: {
          color: isDarkTheme ? "" : "#bababa",
        },
      },
    },
    onClick: (event, elements, chart) => {
      if (
        elements &&
        elements.length > 0 &&
        typeof handleModalShow === "function"
      ) {
        const element = elements[0];
        const datasetIndex = element.datasetIndex;
        const index = element.index;
        const deviceType = chart.data.labels[index];
        const status = chart.data.datasets[datasetIndex].label;
        const value = chart.data.datasets[datasetIndex].data[index];

        const datapoints = deviceTypeToDatapoints[deviceType];
        const interfacetypename = datapoints?.Interfacetypename;

        let filter = { 'StatusFor': deviceType, 'DeviceStatus': status };

        // Add 'IntegrationStatus': 'Integrated' if the Interfacetypename is CCTV
        if (interfacetypename.includes('CCTV')) {
          filter['IntegrationStatus'] = 'Integrated';
        }

        handleModalShow(
          filter,
          'DeviceStatus',
          null,
          { SiteID: [siteId] },
          getcsvFileName(deviceType, status),
          `Device Status Counts`,
          deviceTypeToDatapoints[deviceType] // Pass Datapoints here
        );
      }
    },
  };

  return (
    <div style={{ padding: "10px", height: "90%", display: "flex", justifyContent: "center", alignItems: "center" }}>
      {deviceTypes.length > 0 ? (
        <Bar options={options} data={data} />
      ) : (
        <div style={{ textAlign: "center", fontSize: "16px", fontWeight: "bold", color: "gray" }}>
          No devices found
        </div>
      )}
    </div>
  );
};

export default DeviceStatusStackedBarChart